import qs from 'query-string';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { appActions } from 'store/app/app.actions';
import type { TableView } from 'types/views';

import { viewToUrlFilters } from '../filters/filterParams';

type FiltersName = Record<string, Record<string, string>>;

type UseViewFiltersOptions = {
  currentView: TableView | undefined;
  seedFilters: (rawFilters: Record<string, unknown>) => void;
  replaceFiltersName: (filtersName: FiltersName) => void;
};

/**
 * Syncs the table's filter state to the selected view. When the view changes it
 * merges the view's own filters with the current URL params (the URL wins),
 * seeds those values into filter state, and fetches the human-readable filter
 * name labels — feeding them back to the caller via replaceFiltersName.
 */
export const useViewFilters = ({
  currentView,
  seedFilters,
  replaceFiltersName,
}: UseViewFiltersOptions) => {
  const location = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-runs when the selected view changes, reading the URL params current at that point (matches the prior inline orchestrator effect).
  useEffect(() => {
    if (!currentView) return;

    const newFilters = {
      ...viewToUrlFilters(currentView),
      ...qs.parse(location.search, { arrayFormat: 'comma' }),
    };

    seedFilters(newFilters);

    appActions.getFiltersName({
      data: newFilters,
      callback: (newFiltersName: any) => {
        replaceFiltersName(newFiltersName);
      },
    });
  }, [currentView, seedFilters, replaceFiltersName]);
};
